import { useEffect, useState } from "react";
import { Search, MapPin, Siren, Megaphone, Loader2, Route as RouteIcon, Sun, Moon } from "lucide-react";
import { http } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { AlertsBell } from "@/components/weather/AlertsBell";

export const CitySearch = ({ onPick, testid = "search-city", placeholder, near }) => {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      setLoading(true);
      try { setResults((await http.get("/search", { params: { q, lang, ...(near ? { lat: near.lat, lon: near.lon } : {}) } })).data); } catch { setResults([]); }
      setLoading(false);
    }, 350);
    return () => clearTimeout(id);
  }, [q, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (r) => { onPick(r); setQ(""); setResults([]); };

  return (
    <div className="relative w-full max-w-sm">
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-2 sm:px-4 focus-within:border-sky-400/50">
        {loading ? <Loader2 size={16} className="animate-spin text-slate-400" /> : <Search size={16} className="text-slate-400" />}
        <input data-testid={`${testid}-input`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder || t("Şehir, ilçe veya köy ara…")}
          onKeyDown={(e) => e.key === "Enter" && results[0] && pick(results[0])}
          className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" />
        <button data-testid={`${testid}-button`} onClick={() => results[0] && pick(results[0])} className="hidden text-xs font-mono uppercase text-sky-300 hover:text-sky-200 sm:block">{t("Git")}</button>
      </div>
      {results.length > 0 && (
        <div data-testid={`${testid}-results`} className="glass absolute left-0 right-0 top-12 z-[1200] overflow-hidden rounded-xl">
          {results.map((r, i) => (
            <button key={i} data-testid={`${testid === "search-city" ? "search" : testid}-result-${i}`} onClick={() => pick(r)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-white/5">
              <MapPin size={14} className="text-sky-400" />
              <span className="text-slate-100">{r.name}</span>
              <span className="truncate text-xs text-slate-500">{[r.admin, r.country].filter(Boolean).join(", ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const IconBtn = ({ testid, onClick, title, children, active, className = "" }) => (
  <button data-testid={testid} onClick={onClick} title={title}
    className={`${className || "shrink-0"} rounded-full p-2 transition-colors hover:bg-white/10 ${active ? "bg-sky-500/15 text-sky-300" : "text-slate-300"}`}>{children}</button>
);

export const TopBar = ({ onPick, onReport, onSos, updatedAt, onRoute, routeOpen, favorites, theme, setTheme, lang, setLang }) => {
  const { t } = useI18n();
  return (
  <header className="glass absolute left-3 right-3 top-3 z-[1100] flex h-14 items-center gap-2 rounded-2xl px-3 sm:gap-4 sm:px-4">
    <div data-testid="header-brand-logo" className="flex shrink-0 items-center gap-3">
      <span className="radar-dot" />
      <div className="hidden leading-none sm:block">
        <div className="font-heading text-xl font-extrabold uppercase tracking-wide text-white">Meteo<span className="text-sky-400">Yol</span></div>
        <div className="hidden font-mono text-[10px] uppercase tracking-widest text-slate-500 sm:block">
          {t("Hava & Yol Durumu")} {updatedAt && `· ${new Date(updatedAt).toLocaleTimeString(lang === "en" ? "en-GB" : "tr-TR", { hour: "2-digit", minute: "2-digit" })}`}
        </div>
      </div>
    </div>
    <div className="flex min-w-0 flex-1 justify-center"><CitySearch onPick={onPick} /></div>
    <IconBtn testid="route-panel-trigger" onClick={onRoute} title={t("Rota")} active={routeOpen}><RouteIcon size={18} /></IconBtn>
    <AlertsBell favorites={favorites} onPick={onPick} />
    <IconBtn className="hidden shrink-0 sm:block" testid="theme-toggle-btn" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} title={theme === "dark" ? t("Açık tema") : t("Koyu tema")}>
      {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
    </IconBtn>
    <button data-testid="lang-toggle-btn" onClick={() => setLang(lang === "tr" ? "en" : "tr")}
      className="shrink-0 rounded-full border border-white/10 px-2 py-1 font-mono text-[11px] font-semibold text-slate-300 hover:bg-white/10">{lang === "tr" ? "EN" : "TR"}</button>
    <button data-testid="road-report-modal-trigger" onClick={onReport}
      className="hidden items-center gap-2 rounded-full border border-orange-400/40 bg-orange-500/10 px-4 py-2 text-sm font-semibold text-orange-200 transition-colors hover:bg-orange-500/20 md:flex">
      <Megaphone size={16} /> {t("Yol Raporu")}
    </button>
    <button data-testid="emergency-sos-button" onClick={onSos}
      className="sos-btn keep-white flex shrink-0 items-center gap-2 rounded-full bg-red-600 px-3 sm:px-4 py-2 font-heading text-base font-bold uppercase tracking-wide text-white hover:bg-red-500">
      <Siren size={17} /> <span className="hidden sm:inline">{t("Acil")}</span> 112
    </button>
  </header>
  );
};
